import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  WxbAlert,
  WxbButton,
  WxbCheckbox,
  WxbCollapse,
  WxbDataTable,
  WxbDrawer,
  WxbEmpty,
  WxbFormField,
  WxbInput,
  WxbInputNumber,
  WxbModal,
  WxbSearchInput,
  WxbSelect,
  WxbSpinner,
  WxbSwitch,
  WxbTableActionCell,
  WxbTag,
  WxbTextarea,
} from '../wxb-ui';
import { resourceNodeEquipmentCatalogApi } from '../../services/resourceNodeEquipmentCatalogApi';
import type {
  ResourceNodeEquipmentCatalog,
  ResourceNodeEquipmentCapabilityDefinition,
  ResourceNodeEquipmentCapabilityPayload,
  ResourceNodeEquipmentCategory,
  ResourceNodeEquipmentCategoryPayload,
  ResourceNodeEquipmentModel,
  ResourceNodeEquipmentModelPayload,
} from '../../types/resourceNodeEquipmentCatalog';
import './EquipmentCatalogDrawer.css';

const DEFAULT_FACILITY = (process.env.REACT_APP_PROD_RESOURCE_FACILITY_CODES || 'F1')
  .split(',')[0]?.trim().toUpperCase() || 'F1';

interface EquipmentCatalogDrawerProps {
  open: boolean;
  canWrite: boolean;
  onClose: () => void;
  onCatalogChanged?: () => void;
}

const emptyCategory = (): ResourceNodeEquipmentCategoryPayload => ({
  categoryName: '',
  description: null,
  sortOrder: 0,
  isActive: true,
});

const emptyModel = (categoryId = 0): ResourceNodeEquipmentModelPayload => ({
  categoryId,
  modelName: '',
  description: null,
  sortOrder: 0,
  isActive: true,
  facilityCode: DEFAULT_FACILITY,
  capabilities: [],
  stateProfileId: null,
});

const emptyCapability = (): ResourceNodeEquipmentCapabilityPayload => ({
  capabilityName: '',
  capacityDimension: 'NONE',
  description: null,
  sortOrder: 0,
  isActive: true,
});

const CAPABILITY_DIMENSION_OPTIONS = [
  { value: 'NONE', label: '无规模（无单位）' },
  { value: 'VOLUME', label: '体积（L）' },
  { value: 'FLOW', label: '流量（L/H）' },
  { value: 'AREA', label: '面积（M2）' },
  { value: 'COUNT', label: '数量（EA）' },
] as const;

const capabilityDimensionLabel = (dimension: ResourceNodeEquipmentCapabilityDefinition['capacityDimension']) =>
  CAPABILITY_DIMENSION_OPTIONS.find((item) => item.value === dimension)?.label ?? dimension;

const errorText = (error: any, fallback: string): string =>
  error?.response?.data?.error || error?.message || fallback;

const EquipmentCatalogDrawer: React.FC<EquipmentCatalogDrawerProps> = ({
  open,
  canWrite,
  onClose,
  onCatalogChanged,
}) => {
  const [catalog, setCatalog] = useState<ResourceNodeEquipmentCatalog>({
    categories: [],
    models: [],
    capabilityDefinitions: [],
    stateProfiles: [],
  });
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [expandedTypeIds, setExpandedTypeIds] = useState<React.Key[]>([]);
  const [historySearch, setHistorySearch] = useState('');
  const [historyTypeFilter, setHistoryTypeFilter] = useState<number | 'ALL'>('ALL');
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [modelModalOpen, setModelModalOpen] = useState(false);
  const [capabilityDrawerOpen, setCapabilityDrawerOpen] = useState(false);
  const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState<ResourceNodeEquipmentCategory | null>(null);
  const [editingModel, setEditingModel] = useState<ResourceNodeEquipmentModel | null>(null);
  const [editingCapability, setEditingCapability] = useState<ResourceNodeEquipmentCapabilityDefinition | null>(null);
  const [categoryDraft, setCategoryDraft] = useState<ResourceNodeEquipmentCategoryPayload>(emptyCategory);
  const [modelDraft, setModelDraft] = useState<ResourceNodeEquipmentModelPayload>(emptyModel);
  const [capabilityDraft, setCapabilityDraft] = useState<ResourceNodeEquipmentCapabilityPayload>(emptyCapability);
  const [saving, setSaving] = useState(false);

  const loadCatalog = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const result = await resourceNodeEquipmentCatalogApi.getCatalog(DEFAULT_FACILITY);
      setCatalog(result);
      setExpandedTypeIds((current) => {
        const validIds = new Set(result.categories.map((item) => item.id));
        const retained = current.filter((key) => validIds.has(Number(key)));
        return retained.length > 0 ? retained : result.categories.slice(0, 1).map((item) => item.id);
      });
    } catch (error) {
      setLoadError(errorText(error, '设备字典加载失败'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) void loadCatalog();
  }, [loadCatalog, open]);

  const categoryNameById = useMemo(
    () => new Map(catalog.categories.map((item) => [item.id, item.categoryName])),
    [catalog.categories],
  );

  const reusableModels = useMemo(
    () => catalog.models.filter((item) => item.selectableForNewNodes),
    [catalog.models],
  );

  const historicalModels = useMemo(
    () => catalog.models.filter((item) => !item.selectableForNewNodes),
    [catalog.models],
  );

  const reusableModelsByType = useMemo(() => {
    const grouped = new Map<number, ResourceNodeEquipmentModel[]>();
    reusableModels.forEach((item) => {
      grouped.set(item.categoryId, [...(grouped.get(item.categoryId) ?? []), item]);
    });
    return grouped;
  }, [reusableModels]);

  const historicalCountByType = useMemo(() => {
    const grouped = new Map<number, number>();
    historicalModels.forEach((item) => {
      grouped.set(item.categoryId, (grouped.get(item.categoryId) ?? 0) + 1);
    });
    return grouped;
  }, [historicalModels]);

  const filteredHistoricalModels = useMemo(() => {
    const keyword = historySearch.trim().toLowerCase();
    return historicalModels.filter((item) => {
      if (historyTypeFilter !== 'ALL' && item.categoryId !== historyTypeFilter) return false;
      if (!keyword) return true;
      const typeName = categoryNameById.get(item.categoryId) ?? item.categoryName;
      return item.modelName.toLowerCase().includes(keyword) || typeName.toLowerCase().includes(keyword);
    });
  }, [categoryNameById, historicalModels, historySearch, historyTypeFilter]);

  const reusableModelCountByType = useMemo(
    () => new Map(
      catalog.categories.map((item) => [item.id, reusableModelsByType.get(item.id)?.length ?? 0]),
    ),
    [catalog.categories, reusableModelsByType],
  );

  const openCreateCategory = () => {
    setEditingCategory(null);
    setCategoryDraft(emptyCategory());
    setCategoryModalOpen(true);
  };

  const openCreateCapability = () => {
    setEditingCapability(null);
    setCapabilityDraft(emptyCapability());
    setCapabilityModalOpen(true);
  };

  const openEditCapability = (item: ResourceNodeEquipmentCapabilityDefinition) => {
    setEditingCapability(item);
    setCapabilityDraft({
      capabilityName: item.capabilityName,
      capacityDimension: item.capacityDimension,
      description: item.description,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
    setCapabilityModalOpen(true);
  };

  const openEditCategory = (item: ResourceNodeEquipmentCategory) => {
    setEditingCategory(item);
    setCategoryDraft({
      categoryName: item.categoryName,
      description: item.description,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
    });
    setCategoryModalOpen(true);
  };

  const openCreateModel = (categoryId: number) => {
    setEditingModel(null);
    setModelDraft(emptyModel(categoryId));
    setModelModalOpen(true);
  };

  const openEditModel = (item: ResourceNodeEquipmentModel) => {
    setEditingModel(item);
    setModelDraft({
      categoryId: item.categoryId,
      modelName: item.modelName,
      description: item.description,
      sortOrder: item.sortOrder,
      isActive: item.isActive,
      facilityCode: DEFAULT_FACILITY,
      capabilities: item.capabilities.map((capability) => ({
        capabilityId: capability.capabilityId,
        minCapacity: capability.minCapacity,
        maxCapacity: capability.maxCapacity,
      })),
      stateProfileId: item.stateProfileId,
    });
    setModelModalOpen(true);
  };

  const notifyChanged = async (successMessage: string) => {
    message.success(successMessage);
    await loadCatalog();
    onCatalogChanged?.();
  };

  const saveCategory = async () => {
    if (!categoryDraft.categoryName.trim()) {
      message.error('请填写设备类型名称');
      return;
    }
    setSaving(true);
    try {
      if (editingCategory) await resourceNodeEquipmentCatalogApi.updateCategory(editingCategory.id, categoryDraft);
      else await resourceNodeEquipmentCatalogApi.createCategory(categoryDraft);
      setCategoryModalOpen(false);
      await notifyChanged(editingCategory ? '设备类型已更新' : '设备类型已创建');
    } catch (error) {
      message.error(errorText(error, '设备类型保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const saveCapability = async () => {
    if (!capabilityDraft.capabilityName.trim()) {
      message.error('请填写能力名称');
      return;
    }
    setSaving(true);
    try {
      if (editingCapability) {
        await resourceNodeEquipmentCatalogApi.updateCapability(editingCapability.id, capabilityDraft);
      } else {
        await resourceNodeEquipmentCatalogApi.createCapability(capabilityDraft);
      }
      setCapabilityModalOpen(false);
      await notifyChanged(editingCapability ? '设备能力已更新' : '设备能力已创建');
    } catch (error) {
      message.error(errorText(error, '设备能力保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const saveModel = async () => {
    if (!modelDraft.categoryId || !modelDraft.modelName.trim()) {
      message.error('请选择设备类型并填写型号名称');
      return;
    }
    const incompleteCapability = modelDraft.capabilities.find((item) => {
      const definition = catalog.capabilityDefinitions.find((candidate) => candidate.id === item.capabilityId);
      return definition?.capacityDimension !== 'NONE'
        && (item.minCapacity === null || item.maxCapacity === null);
    });
    if (incompleteCapability) {
      message.error('请填写已选能力的最小值和最大值');
      return;
    }
    setSaving(true);
    try {
      if (editingModel) await resourceNodeEquipmentCatalogApi.updateModel(editingModel.id, modelDraft);
      else await resourceNodeEquipmentCatalogApi.createModel(modelDraft);
      setModelModalOpen(false);
      await notifyChanged(editingModel ? '设备型号已更新' : '设备型号已创建');
    } catch (error) {
      message.error(errorText(error, '设备型号保存失败'));
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = async (item: ResourceNodeEquipmentCategory) => {
    try {
      await resourceNodeEquipmentCatalogApi.updateCategory(item.id, {
        categoryName: item.categoryName,
        description: item.description,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      });
      await notifyChanged(item.isActive ? '设备类型已停用，已有设备不受影响' : '设备类型已启用');
    } catch (error) {
      message.error(errorText(error, '状态更新失败'));
    }
  };

  const toggleCapabilityStatus = async (item: ResourceNodeEquipmentCapabilityDefinition) => {
    try {
      await resourceNodeEquipmentCatalogApi.updateCapability(item.id, {
        capabilityName: item.capabilityName,
        capacityDimension: item.capacityDimension,
        description: item.description,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
      });
      await notifyChanged(item.isActive
        ? '能力已停用，已有引用保留'
        : '能力已启用');
    } catch (error) {
      message.error(errorText(error, '能力状态更新失败'));
    }
  };

  const toggleCapability = (definition: ResourceNodeEquipmentCapabilityDefinition, checked: boolean) => {
    setModelDraft((current) => ({
      ...current,
      capabilities: checked
        ? [...current.capabilities, {
          capabilityId: definition.id,
          minCapacity: null,
          maxCapacity: null,
        }]
        : current.capabilities.filter((item) => item.capabilityId !== definition.id),
    }));
  };

  const updateCapabilityRange = (
    capabilityId: number,
    field: 'minCapacity' | 'maxCapacity',
    value: number | null,
  ) => {
    setModelDraft((current) => ({
      ...current,
      capabilities: current.capabilities.map((item) => (
        item.capabilityId === capabilityId ? { ...item, [field]: value } : item
      )),
    }));
  };

  const toggleModel = async (item: ResourceNodeEquipmentModel) => {
    try {
      await resourceNodeEquipmentCatalogApi.updateModel(item.id, {
        categoryId: item.categoryId,
        modelName: item.modelName,
        description: item.description,
        sortOrder: item.sortOrder,
        isActive: !item.isActive,
        facilityCode: DEFAULT_FACILITY,
        capabilities: item.capabilities.map((capability) => ({
          capabilityId: capability.capabilityId,
          minCapacity: capability.minCapacity,
          maxCapacity: capability.maxCapacity,
        })),
        stateProfileId: item.stateProfileId,
      });
      await notifyChanged(item.isActive ? '型号已停用，已有设备不受影响' : '型号已启用');
    } catch (error) {
      message.error(errorText(error, '状态更新失败'));
    }
  };

  const typeColumns: ColumnsType<ResourceNodeEquipmentCategory> = [
    {
      title: '设备类型',
      dataIndex: 'categoryName',
      render: (value, item) => (
        <div className="equip-catalog-type-cell">
          <strong>{value}</strong>
          <span>{item.description || '暂无说明'}</span>
        </div>
      ),
    },
    {
      title: '可复用型号',
      width: 112,
      render: (_, item) => `${reusableModelCountByType.get(item.id) ?? 0} 个`,
    },
    {
      title: '历史数据',
      width: 104,
      render: (_, item) => {
        const count = historicalCountByType.get(item.id) ?? 0;
        return count > 0 ? <WxbTag color="neutral">{count} 条</WxbTag> : '—';
      },
    },
    { title: '关联设备', dataIndex: 'usageCount', width: 94, render: (value) => `${value} 台` },
    {
      title: '状态',
      width: 82,
      render: (_, item) => <WxbTag color={item.isActive ? 'green' : 'neutral'}>{item.isActive ? '启用' : '停用'}</WxbTag>,
    },
    {
      title: '操作',
      width: 236,
      render: (_, item) => canWrite ? (
        <WxbTableActionCell actions={[
          {
            key: 'create-model',
            label: '新增型号',
            disabled: !item.isActive,
            onClick: () => openCreateModel(item.id),
          },
          { key: 'edit', label: '编辑类型', onClick: () => openEditCategory(item) },
          { key: 'toggle', label: item.isActive ? '停用' : '启用', onClick: () => void toggleCategory(item) },
        ]} maxInline={2} />
      ) : null,
    },
  ];

  const modelColumns: ColumnsType<ResourceNodeEquipmentModel> = [
    {
      title: '型号',
      dataIndex: 'modelName',
    },
    {
      title: '排产能力',
      width: 220,
      render: (_, item) => item.capabilities.length > 0 ? (
        <div className="equip-catalog-capability-tags">
          {item.capabilities.slice(0, 2).map((capability) => (
            <WxbTag key={capability.capabilityId} color="blue">
              {capability.capabilityName}
              {capability.capacityDimension === 'NONE'
                ? ''
                : ` ${capability.minCapacity}–${capability.maxCapacity} ${capability.canonicalUnit}`}
            </WxbTag>
          ))}
          {item.capabilities.length > 2 && <span>+{item.capabilities.length - 2}</span>}
        </div>
      ) : <WxbTag color="amber">未配置</WxbTag>,
    },
    {
      title: '状态模型',
      width: 130,
      render: (_, item) => item.stateProfileName || <WxbTag color="amber">未关联</WxbTag>,
    },
    { title: '关联设备', dataIndex: 'usageCount', width: 94, render: (value) => `${value} 台` },
    {
      title: '状态',
      width: 82,
      render: (_, item) => <WxbTag color={item.isActive ? 'green' : 'neutral'}>{item.isActive ? '启用' : '停用'}</WxbTag>,
    },
    {
      title: '操作',
      width: 150,
      render: (_, item) => canWrite ? (
        <WxbTableActionCell actions={[
          { key: 'edit', label: '编辑', onClick: () => openEditModel(item) },
          { key: 'toggle', label: item.isActive ? '停用' : '启用', onClick: () => void toggleModel(item) },
        ]} />
      ) : null,
    },
  ];

  const capabilityColumns: ColumnsType<ResourceNodeEquipmentCapabilityDefinition> = [
    {
      title: '能力名称',
      dataIndex: 'capabilityName',
      render: (value, item) => (
        <div className="equip-catalog-type-cell">
          <strong>{value}</strong>
          <span>{item.description || '暂无说明'}</span>
        </div>
      ),
    },
    {
      title: '主规模维度',
      width: 150,
      render: (_, item) => capabilityDimensionLabel(item.capacityDimension),
    },
    {
      title: '引用',
      width: 150,
      render: (_, item) => `${item.modelUsageCount} 个型号 · ${item.demandUsageCount} 条工序`,
    },
    {
      title: '状态',
      width: 82,
      render: (_, item) => (
        <WxbTag color={item.isActive ? 'green' : 'neutral'}>
          {item.isActive ? '启用' : '停用'}
        </WxbTag>
      ),
    },
    {
      title: '操作',
      width: 150,
      render: (_, item) => canWrite ? (
        <WxbTableActionCell actions={[
          { key: 'edit', label: '编辑', onClick: () => openEditCapability(item) },
          {
            key: 'toggle',
            label: item.isActive ? '停用' : '启用',
            onClick: () => void toggleCapabilityStatus(item),
          },
        ]} />
      ) : null,
    },
  ];

  const historyColumns: ColumnsType<ResourceNodeEquipmentModel> = [
    { title: '历史名称', dataIndex: 'modelName' },
    {
      title: '设备类型',
      width: 160,
      render: (_, item) => categoryNameById.get(item.categoryId) ?? item.categoryName,
    },
    { title: '关联设备', dataIndex: 'usageCount', width: 94, render: (value) => `${value} 台` },
    {
      title: '用途',
      width: 112,
      render: () => <WxbTag color="neutral">历史关联</WxbTag>,
    },
  ];

  const renderModelChildren = (type: ResourceNodeEquipmentCategory) => {
    const models = reusableModelsByType.get(type.id) ?? [];
    return (
      <div className="equip-catalog-model-children">
        <div className="equip-catalog-child-heading">
          <div>
            <strong>{type.categoryName} · 设备型号</strong>
            <span>型号属于此设备类型；设备形态在具体设备实例上统一维护。</span>
          </div>
          {canWrite && (
            <WxbButton
              size="sm"
              variant="secondary"
              disabled={!type.isActive}
              onClick={() => openCreateModel(type.id)}
            >
              新增型号
            </WxbButton>
          )}
        </div>
        {models.length > 0 ? (
          <WxbDataTable<ResourceNodeEquipmentModel>
            rowKey="id"
            density="compact"
            pagination={false}
            columns={modelColumns}
            dataSource={models}
            containered={false}
          />
        ) : (
          <WxbEmpty
            className="equip-catalog-model-empty"
            description={type.isActive
              ? '暂无可复用型号，可点击右上角新增'
              : '设备类型已停用，不能新增型号'}
          />
        )}
      </div>
    );
  };

  return (
    <>
      <WxbDrawer
        open={open}
        width={940}
        title="设备类型与型号字典"
        onClose={onClose}
        destroyOnClose
        extra={<WxbButton variant="ghost" size="sm" onClick={() => void loadCatalog()}>刷新</WxbButton>}
      >
        <WxbAlert className="equip-catalog-alert">
          设备型号维护排产真正使用的能力、规模范围和状态模型。设备实例通过内部 ID 关联型号；位置、当前状态、拓扑和并行容量仍在实例层维护。
        </WxbAlert>
        {loading ? (
          <div className="equip-catalog-loading"><WxbSpinner /><span>加载设备字典...</span></div>
        ) : loadError ? (
          <WxbAlert variant="error">{loadError}</WxbAlert>
        ) : (
          <div className="equip-catalog-panel">
            <div className="equip-catalog-toolbar">
              <span>
                {catalog.categories.length} 个设备类型 · {reusableModels.length} 个可复用型号
              </span>
              <div className="equip-catalog-toolbar-actions">
                <WxbButton
                  size="sm"
                  variant="secondary"
                  onClick={() => setCapabilityDrawerOpen(true)}
                >
                  可执行能力字典
                </WxbButton>
                {canWrite && <WxbButton size="sm" onClick={openCreateCategory}>新增设备类型</WxbButton>}
              </div>
            </div>

            <WxbDataTable<ResourceNodeEquipmentCategory>
              rowKey="id"
              density="compact"
              pagination={false}
              columns={typeColumns}
              dataSource={catalog.categories}
              emptyState={{ description: '暂无设备类型' }}
              expandable={{
                expandedRowKeys: expandedTypeIds,
                onExpandedRowsChange: (keys) => setExpandedTypeIds([...keys]),
                expandedRowRender: renderModelChildren,
                rowExpandable: () => true,
                indentSize: 16,
              }}
            />

            {historicalModels.length > 0 && (
              <WxbCollapse
                className="equip-catalog-history"
                items={[{
                  key: 'history',
                  label: `历史兼容数据（${historicalModels.length} 条，仅用于保持已有设备关联）`,
                  children: (
                    <div className="equip-catalog-history-content">
                      <WxbAlert>
                        这些记录由历史设备一对一回填，不是真正的可复用型号；不可新增引用、不可编辑，也不会进入 Excel 模板。
                      </WxbAlert>
                      <div className="equip-catalog-history-toolbar">
                        <WxbSearchInput
                          value={historySearch}
                          onChange={setHistorySearch}
                          placeholder="搜索历史名称或设备类型"
                          allowClear
                        />
                        <WxbSelect
                          value={historyTypeFilter}
                          onChange={(value) => setHistoryTypeFilter(value === 'ALL' ? 'ALL' : Number(value))}
                          options={[
                            { value: 'ALL', label: `全部设备类型 (${historicalModels.length})` },
                            ...catalog.categories.map((item) => ({
                              value: item.id,
                              label: `${item.categoryName} (${historicalCountByType.get(item.id) ?? 0})`,
                            })),
                          ]}
                        />
                      </div>
                      <WxbDataTable<ResourceNodeEquipmentModel>
                        rowKey="id"
                        density="compact"
                        columns={historyColumns}
                        dataSource={filteredHistoricalModels}
                        pagination={filteredHistoricalModels.length > 10
                          ? { pageSize: 10, showSizeChanger: false }
                          : false}
                        emptyState={{ description: '没有符合条件的历史数据' }}
                      />
                    </div>
                  ),
                }]}
              />
            )}
          </div>
        )}
      </WxbDrawer>

      <WxbDrawer
        open={capabilityDrawerOpen}
        width={780}
        title="可执行能力字典"
        onClose={() => setCapabilityDrawerOpen(false)}
        destroyOnClose
      >
        <WxbAlert className="equip-catalog-alert">
          能力名称可持续修改；内部编码由系统生成且不对用户展示。能力被型号或工序引用后，主规模维度和标准单位将锁定。
        </WxbAlert>
        <div className="equip-catalog-toolbar">
          <span>{catalog.capabilityDefinitions.length} 项能力</span>
          {canWrite && <WxbButton size="sm" onClick={openCreateCapability}>新增能力</WxbButton>}
        </div>
        <WxbDataTable<ResourceNodeEquipmentCapabilityDefinition>
          rowKey="id"
          density="compact"
          pagination={false}
          columns={capabilityColumns}
          dataSource={catalog.capabilityDefinitions}
          emptyState={{ description: '暂无可执行能力' }}
        />
      </WxbDrawer>

      <WxbModal
        open={capabilityModalOpen}
        title={editingCapability ? '编辑可执行能力' : '新增可执行能力'}
        okText="保存"
        onOk={() => void saveCapability()}
        onCancel={() => setCapabilityModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <div className="equip-catalog-form">
          <WxbFormField label="能力名称" required>
            <WxbInput
              value={capabilityDraft.capabilityName}
              onChange={(event) => setCapabilityDraft((current) => ({
                ...current,
                capabilityName: event.target.value,
              }))}
              placeholder="例如：离心"
            />
          </WxbFormField>
          <WxbFormField
            label="主规模维度"
            required
            helpText={editingCapability && (editingCapability.modelUsageCount + editingCapability.demandUsageCount > 0)
              ? '已有引用，主规模维度和标准单位不可修改。'
              : '标准单位由系统根据主规模维度自动确定。'}
          >
            <WxbSelect
              value={capabilityDraft.capacityDimension}
              disabled={Boolean(editingCapability
                && (editingCapability.modelUsageCount + editingCapability.demandUsageCount > 0))}
              options={CAPABILITY_DIMENSION_OPTIONS.map((item) => ({ ...item }))}
              onChange={(value) => setCapabilityDraft((current) => ({
                ...current,
                capacityDimension: value as ResourceNodeEquipmentCapabilityPayload['capacityDimension'],
              }))}
            />
          </WxbFormField>
          <WxbFormField label="说明">
            <WxbTextarea
              rows={3}
              value={capabilityDraft.description ?? ''}
              onChange={(event) => setCapabilityDraft((current) => ({
                ...current,
                description: event.target.value || null,
              }))}
              placeholder="说明该能力在什么工序中使用"
            />
          </WxbFormField>
          <div className="equip-catalog-form-row">
            <WxbInputNumber
              label="排序"
              min={0}
              value={capabilityDraft.sortOrder}
              onChange={(value) => setCapabilityDraft((current) => ({
                ...current,
                sortOrder: Number(value ?? 0),
              }))}
            />
            <WxbFormField label="启用状态">
              <WxbSwitch
                checked={capabilityDraft.isActive}
                onChange={(value) => setCapabilityDraft((current) => ({ ...current, isActive: value }))}
              />
            </WxbFormField>
          </div>
        </div>
      </WxbModal>

      <WxbModal
        open={categoryModalOpen}
        title={editingCategory ? '编辑设备类型' : '新增设备类型'}
        okText="保存"
        onOk={() => void saveCategory()}
        onCancel={() => setCategoryModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
      >
        <div className="equip-catalog-form">
          <WxbFormField label="设备类型名称" required>
            <WxbInput
              value={categoryDraft.categoryName}
              onChange={(event) => setCategoryDraft((current) => ({ ...current, categoryName: event.target.value }))}
              placeholder="例如：反应器"
            />
          </WxbFormField>
          <WxbFormField label="说明">
            <WxbTextarea
              rows={3}
              value={categoryDraft.description ?? ''}
              onChange={(event) => setCategoryDraft((current) => ({ ...current, description: event.target.value || null }))}
            />
          </WxbFormField>
          <div className="equip-catalog-form-row">
            <WxbInputNumber
              label="排序"
              min={0}
              value={categoryDraft.sortOrder}
              onChange={(value) => setCategoryDraft((current) => ({ ...current, sortOrder: Number(value ?? 0) }))}
            />
            <WxbFormField label="启用状态">
              <WxbSwitch
                checked={categoryDraft.isActive}
                onChange={(value) => setCategoryDraft((current) => ({ ...current, isActive: value }))}
              />
            </WxbFormField>
          </div>
        </div>
      </WxbModal>

      <WxbModal
        open={modelModalOpen}
        title={editingModel
          ? '编辑设备型号'
          : `为「${categoryNameById.get(modelDraft.categoryId) ?? '设备类型'}」新增型号`}
        okText="保存"
        onOk={() => void saveModel()}
        onCancel={() => setModelModalOpen(false)}
        confirmLoading={saving}
        destroyOnClose
        width={760}
      >
        <div className="equip-catalog-form">
          <WxbFormField label="所属设备类型" required>
            <WxbSelect
              value={modelDraft.categoryId || undefined}
              disabled
              onChange={(value) => setModelDraft((current) => ({ ...current, categoryId: Number(value) }))}
              options={catalog.categories.filter((item) => item.isActive || item.id === modelDraft.categoryId).map((item) => ({
                value: item.id,
                label: item.categoryName,
              }))}
              placeholder="请从设备类型下新增型号"
            />
          </WxbFormField>
          <WxbFormField label="型号名称" required>
            <WxbInput
              value={modelDraft.modelName}
              onChange={(event) => setModelDraft((current) => ({ ...current, modelName: event.target.value }))}
              placeholder="例如：BR-101"
            />
          </WxbFormField>
          <WxbFormField label="说明">
            <WxbTextarea
              rows={3}
              value={modelDraft.description ?? ''}
              onChange={(event) => setModelDraft((current) => ({ ...current, description: event.target.value || null }))}
            />
          </WxbFormField>
          <WxbFormField
            label="可执行能力"
            helpText="只有会改变设备候选或处理规模的能力才在这里维护。"
          >
            <div className="equip-catalog-capability-editor">
              {catalog.capabilityDefinitions.filter((item) => item.isActive).map((definition) => {
                const selected = modelDraft.capabilities.find((item) => item.capabilityId === definition.id);
                return (
                  <div className="equip-catalog-capability-row" key={definition.id}>
                    <div className="equip-catalog-capability-main">
                      <WxbCheckbox
                        checked={Boolean(selected)}
                        onChange={(checked) => toggleCapability(definition, checked)}
                      >
                        {definition.capabilityName}
                      </WxbCheckbox>
                      <span>{definition.description}</span>
                    </div>
                    {selected && definition.capacityDimension !== 'NONE' && (
                      <div className="equip-catalog-capability-range">
                        <WxbInputNumber
                          min={0}
                          value={selected.minCapacity}
                          onChange={(value) => updateCapabilityRange(definition.id, 'minCapacity', value === null ? null : Number(value))}
                          placeholder="最小值"
                        />
                        <span>至</span>
                        <WxbInputNumber
                          min={0}
                          value={selected.maxCapacity}
                          onChange={(value) => updateCapabilityRange(definition.id, 'maxCapacity', value === null ? null : Number(value))}
                          placeholder="最大值"
                        />
                        <WxbTag color="neutral">{definition.canonicalUnit}</WxbTag>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </WxbFormField>
          <WxbFormField
            label="状态模型"
            helpText="状态模型负责就绪条件和合法状态动作；不在型号上重复维护 CIP/SIP 开关。"
          >
            <WxbSelect
              value={modelDraft.stateProfileId ?? undefined}
              allowClear
              placeholder={catalog.stateProfiles.length > 0 ? '选择状态模型' : '当前设施暂无状态模型'}
              options={catalog.stateProfiles.map((item) => ({ value: item.id, label: item.profileName }))}
              onChange={(value) => setModelDraft((current) => ({
                ...current,
                stateProfileId: value === undefined || value === null ? null : Number(value),
              }))}
            />
          </WxbFormField>
          <div className="equip-catalog-form-row">
            <WxbInputNumber
              label="排序"
              min={0}
              value={modelDraft.sortOrder}
              onChange={(value) => setModelDraft((current) => ({ ...current, sortOrder: Number(value ?? 0) }))}
            />
            <WxbFormField label="启用状态">
              <WxbSwitch
                checked={modelDraft.isActive}
                onChange={(value) => setModelDraft((current) => ({ ...current, isActive: value }))}
              />
            </WxbFormField>
          </div>
        </div>
      </WxbModal>
    </>
  );
};

export default EquipmentCatalogDrawer;
